/* eslint-disable arrow-body-style, object-curly-newline,no-unused-vars */
/* global React */
import { useEffect, useRef, useState, useTransition } from 'react'
import { FormControl, FormControlLabel, Checkbox, FormHelperText, Grid } from '@mui/material'
import useMemoRef from '@platform/react/hook/useMemoRef'
import { useStyles } from '@platform/react/hook'
import PlatformEvent from 'lib/util/event'
import OfflineHOC from 'platform/react/hoc/offline'
import { def } from 'lib/util'
import Icon from 'ui/Element/Icon'

const styles = (theme, { inline }) => ({
  root: {
    width: inline ? '100%' : 'auto',
  },
  link: {
    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'none',
      fontWeight: 'bold',
    },
  },
  check: {
    padding: '0.25rem',
  },
  label: {
    marginLeft: 0,
    justifyContent: inline ? 'space-between' : 'flex-start',
    userSelect: 'none',
  },
  helperText: {
    margin: 0,
  },
  ...theme.custom.checkField,
})

/**
 * Checkbox Element
 *
 * providing checked attribute to the element will throw a React warning in regard to
 * switching from uncontrolled to controlled component,
 * so we need to convert checked to defaultChecked
 *
 * controlled component - we need to propagate new (props) checked on each change.
 * uncontrolled component - we set defaults (props) and let component take care of itself,
 * until we force a new set of props.
 *
 * More info: https://fb.me/react-controlled-components
 *
 * @param {Object} forwardedRef                     ref to the element
 * @param {string} [label]                          label to display
 * @param {boolean} [labelStart]                    whether the label should be placed at the start
 * @param {Object|string} [icon]                    optional icon to show instead of the checkbox
 * @param {Object|string} [checkedIcon]             optional icon to show when checked
 * @param {boolean|string|number} [checkedValue]    field value when checked
 * @param {boolean|string|number} [uncheckedValue]  field value when unchecked
 * @param {Object} [events]                         element's event handlers
 * @param {string} [color]                          color for the label
 * @param {boolean} [inline]                        whether the element should take its whole
 *                                                  parent's width
 * @param {string} [size]                           size for the checkbox
 * @param {string} [helperText]                     error text
 * @param {boolean} [error]                         whether to display the `helperText`
 * @param {Object} [props]                          additional props
 * @param {boolean|string|number} [props.value]     value state
 * @param {boolean} [props.checked]                 checked state
 * @returns {React.ReactNode}
 */
const CheckField = ({
  forwardedRef,
  label,
  labelStart,
  icon,
  checkedIcon,
  checkedValue,
  uncheckedValue,
  events,
  color,
  inline,
  size,
  helperText,
  error,
  ...props
}) => {
  const [, startTransition] = useTransition()
  const classes = useStyles(styles, { inline })()

  const checkedValueRef = useRef(checkedValue ?? (props.value || true))
  const uncheckedValueRef = useRef(uncheckedValue ?? false)

  const [value, setValue] = useState(props.checked
    ? checkedValueRef.current
    : uncheckedValueRef.current)

  const [checked, setChecked] = useState(props.checked ?? false)

  const handleChange = (event) => {
    const { checked: isChecked } = event.target
    const newValue = isChecked ? checkedValueRef.current : uncheckedValueRef.current

    setChecked(isChecked)
    setValue(newValue)
    startTransition(() => {
      events?.onChange(new PlatformEvent('change', { value: newValue, checked: isChecked }))
    })
  }

  useEffect(() => {
    const newValue = props.value
    setValue(newValue || false)
    newValue === checkedValueRef.current && setChecked(true)
    newValue === uncheckedValueRef.current && setChecked(false)
  }, [props.value])

  useEffect(() => {
    if (def(props.checked)) {
      const isChecked = props.checked || false
      setChecked(isChecked)
      setValue(isChecked ? checkedValueRef.current : uncheckedValueRef.current)
    }
  }, [props.checked])

  return (
    <FormControl
      className={classes.root}
      error={error}
    >
      <FormControlLabel
        color={color || 'textBlack'}
        className={classes.label}
        labelPlacement={labelStart ? 'start' : 'end'}
        control={
          <Checkbox
            {...props}
            icon={icon
              && <Icon source={icon} />}
            checkedIcon={checkedIcon
              && <Icon source={checkedIcon} />}
            value={value}
            checked={checked}
            ref={forwardedRef}
            onChange={handleChange}
            className={classes.check}
            color={'primary'}
            size={size}
          />
        }
        label={
          <>
            {label && (
              <Grid
                component={'span'}
                className={classes.link}
              >
                {label}
              </Grid>
            )}
          </>
        }
      />
      {helperText && (
        <FormHelperText
          className={classes.helperText}
        >
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  )
}

export default useMemoRef(OfflineHOC(CheckField), props => [
  props.value,
  props.label,
  props.helperText,
  props.checked,
  props.disabled,
  props.required,
  props.style,
  props.error,
])
